<template>
  <section
    id="nominations"
    class="section section--nominations section--notRadius"
  >
    <div class="section__wrapper">
      <h2 class="text-44 text-bold title-mb">Номинации</h2>
      <div v-if="hasNominations === false">
        <h2 class="text-22 text-bold">Сейчас их нет, но скоро появятся</h2>
      </div>
      <ul v-else>
        <div class="nominations__list nominations__list--education">
          <p class="nominations__list-subtitle">
            Образовательный трек
          </p>
          <NominationsCardAdditional
            v-for="(nomination, index) in educationNominations"
            :id="nomination.id"
            :key="nomination.id"
            :img="nomination.img"
            :icon="nomination.icon"
            :title="nomination.title"
            :slug="nomination.slug"
            :description="nomination.description"
            :front-title-width="nomination.frontTitleWidth"
            :style="getNominationFrontStyle(nomination)"
            :class="[
              'nominations__item',
              `nominations__item--${index + 1}`,
              `nominations__item--id-${nomination.id}`,
            ]"
          />
        </div>
        <div class="nominations__list nominations__list--technology">
          <p class="nominations__list-subtitle">
            Технологический трек
          </p>
          <NominationsCardAdditional
            v-for="(nomination, index) in technologyNominations"
            :id="nomination.id"
            :key="nomination.id"
            :img="nomination.img"
            :icon="nomination.icon"
            :title="nomination.title"
            :slug="nomination.slug"
            :description="nomination.description"
            :front-title-width="nomination.frontTitleWidth"
            :style="getNominationFrontStyle(nomination)"
            :class="[
              'nominations__item',
              `nominations__item--${index + 1}`,
              `nominations__item--id-${nomination.id}`,
            ]"
          />
        </div>
      </ul>
    </div>
  </section>
</template>
<script setup lang="ts">
import type { Nomination as StrapiNomination } from "~/types/strapiContent"
import type { StrapiResponseData } from "~/types/strapiData"
import { getNominations } from "@/backend/composables/base"
import {
  nominations,
  type NominationTrack,
  type NominationFrontGlowStyle,
  type NominationFrontImageStyle,
} from "./nominations"

interface NominationCard {
  id: number
  track: NominationTrack
  img: string | null
  icon: string | null
  title: string
  slug?: string
  description: string
  frontTitleWidth: string
  frontImage: NominationFrontImageStyle
  frontGlow: NominationFrontGlowStyle
}

const nominationsData = await getNominations()

const nominationsById = computed(() => {
  const data = Array.isArray(nominationsData)
    ? (nominationsData as StrapiResponseData<StrapiNomination>[])
    : []

  return data.reduce(
    (acc, nomination) => acc.set(nomination.id, nomination),
    new Map<number, StrapiResponseData<StrapiNomination>>(),
  )
})

const nominationCards = computed(() =>
  nominations
    .map((layout) => {
      const nomination = nominationsById.value.get(layout.id)
      if (!nomination?.attributes) return null

      return {
        id: nomination.id,
        track: layout.track,
        img:
          nomination.attributes.photo?.data?.attributes?.formats?.large?.url ??
          nomination.attributes.photo?.data?.attributes?.formats?.medium
            ?.url ??
          nomination.attributes.photo?.data?.attributes?.formats?.small?.url ??
          nomination.attributes.photo?.data?.attributes?.formats?.thumbnail
            ?.url ??
          nomination.attributes.photo?.data?.attributes?.url ??
          null,
        icon: nomination.attributes.icon?.data?.attributes?.url ?? null,
        title: nomination.attributes.name ?? "",
        slug: nomination.attributes.slug ?? undefined,
        description: nomination.attributes.description ?? "",
        frontTitleWidth: layout.frontTitleWidth,
        frontImage: layout.frontImage,
        frontGlow: layout.frontGlow,
      } as NominationCard
    })
    .filter((nomination): nomination is NominationCard => nomination !== null),
)

const getNominationFrontStyle = (nomination: NominationCard) => ({
  "--nom-front-image-width": nomination.frontImage.width,
  "--nom-front-image-top": nomination.frontImage.top ?? "auto",
  "--nom-front-image-right": nomination.frontImage.right ?? "auto",
  "--nom-front-image-bottom": nomination.frontImage.bottom ?? "auto",
  "--nom-front-image-left": nomination.frontImage.left ?? "auto",
  "--nom-front-glow-width": nomination.frontGlow.width,
  "--nom-front-glow-height": nomination.frontGlow.height,
  "--nom-front-glow-top": nomination.frontGlow.top,
  "--nom-front-glow-left": nomination.frontGlow.left,
})

const educationNominations = computed(() =>
  nominationCards.value.filter((nomination) => nomination.track === "education"),
)

const technologyNominations = computed(() =>
  nominationCards.value.filter((nomination) => nomination.track === "technology"),
)

const hasNominations = computed(() => nominationCards.value.length > 0)
</script>
<style lang="scss">
.section {
  &--nominations {
    margin: 0 0 -10px 0;
    padding: 100px 70px 150px;
    @include md {
      margin: 0 0 0 0;
      padding: 70px 36px 130px;
    }
    @include sm {
      padding: 40px 16px 60px;
    }
  }
}
#nominations ul {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0;
  padding: 0;
  list-style: none;

  .nominations__list {
    flex: 1;
    padding: 34px 40px 40px;
    border-radius: 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;

    .nominations__list-subtitle {
      flex: 0 0 100%;
      font-size: 16px;
      font-weight: 700;
      line-height: 1.2;
      margin: 0;
    }

    .nomination.nominations__item {
      flex: 0 0 100%;
      min-height: 0;
      height: auto;
      background-color: #ffffff;
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      min-width: 0;
    }
  }

  .nominations__list--education {
    background: #E9EDFE;

    .nomination.nominations__item--1 {
      flex-basis: calc((100% - 40px) * 450 / 1180);
    }

    .nomination.nominations__item--2 {
      flex-basis: calc((100% - 40px) * 310 / 1180);
    }

    .nomination.nominations__item--3 {
      flex-basis: calc((100% - 40px) * 420 / 1180);
    }

    .nomination.nominations__item--4,
    .nomination.nominations__item--5 {
      flex-basis: calc((100% - 20px) / 2);
    }
  }

  .nominations__list--technology {
    background: #E3DDFE;

    .nomination.nominations__item--1 {
      flex-basis: calc((100% - 20px) * 530 / 1131);
    }

    .nomination.nominations__item--2 {
      flex-basis: calc((100% - 20px) * 601 / 1131);
    }

    .nomination.nominations__item--3,
    .nomination.nominations__item--4,
    .nomination.nominations__item--5 {
      flex-basis: calc((100% - 40px) / 3);
    }
  }

  @media (max-width: 1079.99px) and (min-width: 580px) {
    .nominations__list {
      .nomination.nominations__item {
        min-height: 0;
        height: auto;
      }
    }

    .nominations__list--education {
      .nomination.nominations__item--1 {
        flex-basis: 100%;
      }

      .nomination.nominations__item--2,
      .nomination.nominations__item--3,
      .nomination.nominations__item--4,
      .nomination.nominations__item--5 {
        flex-basis: calc((100% - 20px) / 2);
      }
    }

    .nominations__list--technology {
      .nomination.nominations__item--1,
      .nomination.nominations__item--2,
      .nomination.nominations__item--3,
      .nomination.nominations__item--4 {
        flex-basis: calc((100% - 20px) / 2);
      }

      .nomination.nominations__item--5 {
        flex-basis: 100%;
      }
    }
  }

  @include md {
    .nominations__list {
      padding: 40px 36px;
      gap: 16px;
    }
  }

  @include sm {
    .nominations__list {
      padding: 20px 16px;

      .nomination.nominations__item {
        flex-basis: 100%;
        min-height: 0;
        height: auto;
      }
    }
  }
}
</style>
